'use client';
import dynamic from 'next/dynamic';
import ClientTestimonials from '@/components/home/client-testimonials';
import DedicatedHero from '@/components/dedicated-hero';
import DefaultBreadcrumb from '@/components/common/breadcrumbs';
import DedicatedCard from '@/components/common/dedicated-card';

import MWSVG from '@/components/common/svg';
import MetaTags from '@/components/meta-tag/meta-tag';
import Blog from '@/components/home/blog';

const ContactInfo = dynamic(() => import('@/components/common/contact-info'));
const ContactForm = dynamic(() => import('@/components/common/contact-form'));

const Page = () => {
    return (
        <>
            <MetaTags />
            <DedicatedHero
                className=""
                pageTitle="Dedicated Team Engagement Model"
                isDesc={true}
                desc="Hire a dedicated team of experts to deliver your next-gen software project seamlessly"
                alt="hero image"
                title="hero image"
                width={459}
                height={528}
                path={'dedicated-team'}
                isButton={true}
            />
            <DefaultBreadcrumb currentPage="Dedicated Team" isParent={false} />
            <section className="section section-py">
                <div className="container lg:container mx-auto">
                    <div className="row grid md:grid-cols-12 lg:grid-cols-12">
                        <div className="md:col-span-8">
                            <div className="flex flex-col gap-[30px]">
                                <div>
                                    <p className="fs-md mb-2.5">
                                        Are you looking to build a new software project, but don&apos;t have the in-house
                                        resources to do it yourself? Or maybe you have a team, but they are not the right fit for
                                        the project. If so, then a dedicated team engagement model may be the perfect solution for
                                        you.
                                    </p>
                                    <p className="fs-md mb-0">
                                        With a dedicated team, you will get a team of hand-picked experts to fit your specific
                                        needs. They will work with you every step of the way, from requirements gathering to
                                        development to testing and deployment. And because they are dedicated to your project,
                                        they will be able to give full effort to deliver the best results.
                                    </p>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">What is a Dedicated Team Engagement Model?</h2>
                                    <p className="fs-md mb-2.5">
                                        The dedicated team engagement model is a software development approach that involves
                                        hiring a team of experts to work exclusively on your project. This team will be dedicated
                                        to your project and will work directly with you to ensure that your requirements are met.
                                    </p>
                                    <p className="fs-md mb-2.5">The dedicated team engagement structure works like this:</p>
                                    <ul className="list-decimal text-secondary fs-md mb-0 ml-4">
                                        <li className="mb-1">
                                            You outline your project requirements to the outsourcing company. This includes the
                                            specific features and functionality of your software, as well as your target audience
                                            and budget.
                                        </li>
                                        <li className="mb-1">
                                            The outsourcing company will then hire a team of experts that meet your requirements.
                                            This team will typically consist of developers, designers, and project managers with
                                            experience in your industry.
                                        </li>
                                        <li className="mb-1">
                                            Our team will work directly with you to ensure that your requirements are met. This
                                            includes regular communication, feedback, and approval processes.
                                        </li>
                                        <li className="mb-0">
                                            The team will be dedicated to your project for the duration of the project. This means
                                            that they will be focused solely on your project and will not be assigned to any other
                                            work.
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">What are the Benefits of a Dedicated Hiring Model?</h2>
                                    <p className="fs-md mb-2.5">
                                        Here are some of the benefits of hiring a dedicated team engagement model:
                                    </p>
                                    <ul className="list-none text-secondary fs-md mb-0 dedicated-content ps-0">
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Cost-effectiveness:</span>
                                            Hiring a dedicated team can be more cost-effective than hiring and managing an
                                            in-house team.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Speed:</span>
                                            Dedicated teams can typically deliver projects faster than in-house teams.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Quality:</span>
                                            Dedicated teams are often more experienced and have a better track record of
                                            delivering high-quality projects.
                                        </li>
                                        <li className="mb2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Flexibility:</span>
                                            Dedicated teams can be scaled up or down as needed, making them a great option for
                                            projects with changing requirements.
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">Who is on a Dedicated Team?</h2>
                                    <p className="fs-md mb-2.5">
                                        Your team composition depends on your business requirements. Thus, your development team
                                        may consist of the following:
                                    </p>
                                    <ul className="list-none fw-600 text-textcolor fs-md mb-0 dedicated-content ps-0">
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Front-end and back-end developers
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Full-stack engineers
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Mobile developers
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            UX/UI designers
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            DevOps engineers
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Quality assurance specialists
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Business analysts
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Product managers
                                        </li>
                                        <li className="mb2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Project managers
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">
                                        Why Choose Moweb for a Dedicated Team for Software Development?
                                    </h2>
                                    <p className="fs-md mb-2.5">
                                        There are many benefits to choosing the dedicated team engagement model, including:
                                    </p>
                                    <ul className="list-none text-secondary fs-md mb-0 dedicated-content ps-0">
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Cost-efficiency:</span>
                                            Hiring a dedicated team can be more cost-effective than hiring and managing an
                                            in-house team.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Increased focus:</span>
                                            The dedicated team will be focused solely on your project, which can lead to faster
                                            and more efficient development.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Higher flexibility:</span>
                                            The hand-picked team can be reconfigured as needed to meet your project’s changing
                                            requirements.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Greater control:</span>
                                            You will have complete control over the selection, motivation, and management of a
                                            dedicated team.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Better communication:</span>
                                            Dedicated teams have a better understanding of your needs and requirements, which
                                            leads to better communication and collaboration.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Improved quality:</span>
                                            Dedicated teams have the experience and expertise to deliver high-quality software.
                                        </li>
                                        <li className="mb2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Reduced risk:</span>
                                            Dedicated teams can help you reduce the risk of project failure by providing a team of
                                            experts familiar with your industry and your specific needs.
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">Who is the Dedicated Team Engagement Model Best for?</h2>
                                    <p className="fs-md mb-2.5">
                                        The dedicated team engagement model is a good fit for businesses that need to:
                                    </p>
                                    <ul className="list-none fw-400 text-secondary fs-md mb-0 dedicated-content ps-0">
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Develop a new software product or application
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Enhance an existing software product or application
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Migrate to a new platform
                                        </li>
                                        <li className="mb2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Optimize their software development process
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="md:col-span-4">
                            <DedicatedCard />
                        </div>
                    </div>
                </div>
            </section>

            <section className="">
                <ClientTestimonials />
            </section>
            <Blog />
            <div className="section bg-primary py-100 dark:text-light dark:bg-black">
                <div className="container lg:container md:mx-auto">
                    <div className="row grid md:grid-cols-12 lg:grid-cols-12">
                        <ContactForm />
                        <ContactInfo />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Page;
